import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapPin, UserCheck, X } from "lucide-react";

const API = "/api";

/**
 * Surfaces existing customers whose address shares a street number with the
 * address being typed. Calls onConfirm(customer) when the user confirms a match.
 */
const AddressMatchSuggestion = ({ address, excludeId, onConfirm, confirmLabel = "Yes, same customer" }) => {
  const [matches, setMatches] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setDismissed(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const value = (address || "").trim();
    if (value.length < 3 || !/\d/.test(value)) {
      setMatches([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/customers/match-address`, {
          params: { address: value, exclude_id: excludeId || undefined },
          withCredentials: true,
        });
        setMatches(res.data || []);
      } catch {
        setMatches([]);
      }
    }, 400);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [address, excludeId]);

  if (dismissed || matches.length === 0) return null;

  return (
    <div data-testid="address-match-suggestion" className="mt-2 rounded-lg border border-[#FACC15]/40 bg-[#FACC15]/10 p-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-[#FACC15] text-sm font-semibold flex items-center gap-1.5">
          <MapPin className="w-4 h-4" /> Possible existing customer{matches.length > 1 ? "s" : ""} at this address
        </p>
        <button type="button" data-testid="dismiss-address-match" onClick={() => setDismissed(true)} className="text-[#A1A1AA] hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-2">
        {matches.map((m) => (
          <div key={m.customer_id} data-testid={`address-match-${m.customer_id}`}
            className="flex items-center justify-between gap-3 bg-[#0A0A0A] rounded-md p-2.5 border border-[#27272A]">
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{m.name}</p>
              <p className="text-[#A1A1AA] text-xs truncate">{m.address}</p>
            </div>
            <button
              type="button"
              data-testid={`confirm-address-match-${m.customer_id}`}
              onClick={() => onConfirm(m)}
              className="shrink-0 flex items-center gap-1.5 bg-[#39FF14] text-black text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-[#32d912]"
            >
              <UserCheck className="w-3.5 h-3.5" /> {confirmLabel}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddressMatchSuggestion;
